import { API_URL, PREFIX } from "../../components/Config";
import { checkProductPromotion } from "../epos";
import { resolveCartPlatform, resolveCartChannelId, PLATFORMS } from "../cart";
import {
    transformCartToPromotionPayload,
    formatDateForAPI,
} from "../../components/sales/EposEdit/utils/promotionHelpers";

const getChannelByRole = () =>
    resolveCartPlatform({}) ?? PLATFORMS.TELE_CALLING;

export const getPromotions = async (page = 1, pageSize = 12, search = null, date_filter = 'all', options = {}) => {
    try {
        const token = localStorage.getItem('token');
        const channel =
            options.channels !== undefined && options.channels !== null
                ? options.channels
                : getChannelByRole();
        const response = await fetch(`${API_URL}/api/promotions?channels=${channel}&status=active&page=${page}&page_size=${pageSize}${search ? `&search=${search}` : ''}${date_filter ? `&date_filter=${date_filter}` : ''}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}
export const getPromotionDetails = async (promotionId, options = {}) => {
    try {
        const token = localStorage.getItem('token');
        const channel =
            options.channels !== undefined && options.channels !== null
                ? options.channels
                : getChannelByRole();
        const response = await fetch(`${API_URL}/api/promotions/${promotionId}?channels=${channel}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}

export const getPromotionGetProducts = async (promotionId, options = {}) => {
    try {
        const token = localStorage.getItem('token');
        const channel =
            options.channels !== undefined && options.channels !== null
                ? options.channels
                : getChannelByRole();
        const params = new URLSearchParams();
        params.set('channels', String(channel));
        const slabId = options.slabId ?? options.slab_id;
        if (
            slabId !== undefined &&
            slabId !== null &&
            String(slabId).trim() !== '' &&
            String(slabId).trim() !== '0'
        ) {
            params.set('slab_id', String(slabId).trim());
        }
        const response = await fetch(`${API_URL}/api/promotions/${promotionId}/get-products?${params.toString()}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
};

export const normalizeProductIdsQueryParam = (productId) => {
    if (productId === undefined || productId === null) {
        return "";
    }
    const parts = [];
    const pushTokens = (chunk) => {
        String(chunk ?? "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((id) => parts.push(id));
    };
    if (Array.isArray(productId)) {
        productId.forEach(pushTokens);
    } else {
        pushTokens(productId);
    }
    return [...new Set(parts)].join(",");
};

export const resolveCartLinePromotionId = (line) => {
    const raw =
        line?.promo_id ??
        line?.promosId ??
        line?.promoId ??
        line?.promotion_id;
    if (raw === undefined || raw === null || raw === "") {
        return null;
    }
    const s = String(raw).trim();
    if (s === "" || s === "0") {
        return null;
    }
    return s;
};

export const resolveSlabIdFromEntity = (entity) => {
    if (!entity || typeof entity !== "object") {
        return null;
    }
    const raw =
        entity.slab_id ??
        entity.slabId ??
        entity.promotion_slab_id ??
        entity.promo_slab_id ??
        entity.applied_slab_id ??
        entity.active_slab_id;
    if (raw === undefined || raw === null || raw === "") {
        return null;
    }
    const slab = String(raw).trim();
    return slab !== "" && slab !== "0" ? slab : null;
};

export const resolveCartLineSlabId = (
    line,
    bundleDiscounts = [],
    cartItems = [],
    promotionMeta = null
) => {
    const fromLine = resolveSlabIdFromEntity(line);
    if (fromLine) {
        return fromLine;
    }

    const fromPromo = resolveSlabIdFromEntity(promotionMeta);
    if (fromPromo) {
        return fromPromo;
    }

    const promoId = resolveCartLinePromotionId(line);
    const bundleId = line?.bundle_id ?? line?.bundleId;

    if (Array.isArray(bundleDiscounts) && bundleDiscounts.length > 0) {
        let bundle = null;
        if (bundleId !== undefined && bundleId !== null && String(bundleId).trim() !== "") {
            bundle = bundleDiscounts.find(
                (entry) => String(entry?.bundle_id ?? "").trim() === String(bundleId).trim()
            );
        }
        if (!bundle && promoId) {
            bundle = bundleDiscounts.find(
                (entry) => String(entry?.promotion_id ?? "").trim() === promoId
            );
        }
        const fromBundle = resolveSlabIdFromEntity(bundle);
        if (fromBundle) {
            return fromBundle;
        }
    }

    if (promoId && Array.isArray(cartItems)) {
        for (const item of cartItems) {
            if (resolveCartLinePromotionId(item) !== promoId) {
                continue;
            }
            const fromSibling = resolveSlabIdFromEntity(item);
            if (fromSibling) {
                return fromSibling;
            }
        }
    }

    return null;
};

const collectPromotionBuyProductIds = (promo) => {
    const ids = new Set();
    if (!promo || typeof promo !== "object") {
        return ids;
    }
    const raw = promo.rawData && typeof promo.rawData === "object" ? promo.rawData : promo;
    const lists = [raw?.buy_products, promo?.buyProducts];
    for (let i = 0; i < lists.length; i += 1) {
        const list = lists[i];
        if (!Array.isArray(list)) {
            continue;
        }
        for (let j = 0; j < list.length; j += 1) {
            const p = list[j];
            const pid = String(
                p?.product_id ??
                    p?.buy_product_id ??
                    p?.productId ??
                    p?.id ??
                    ""
            ).trim();
            if (pid && pid !== "0") {
                ids.add(pid);
            }
        }
    }
    return ids;
};

const buyProductSetsOverlap = (a, b) => {
    if (!a?.size || !b?.size) {
        return false;
    }
    for (const id of a) {
        if (b.has(id)) {
            return true;
        }
    }
    return false;
};

const isBuyPromotionCartLineEarly = (line) => {
    if (!line) {
        return false;
    }
    let t = String(
        line?.promo_type ??
            line?.promoType ??
            line?.promosType ??
            ""
    )
        .trim()
        .toUpperCase();
    if (!t && line?.buyOrGet) {
        const b = String(line.buyOrGet).trim().toUpperCase();
        if (b === "BUY" || b === "GET" || b === "FREE") {
            t = b;
        }
    }
    if (t === "GET" || t === "FREE") {
        return false;
    }
    return true;
};

export const resolvePreviousPromotionIdForChange = ({
    targetPromotionId,
    cartItem = null,
    appliedPromotions = [],
    cartItems = [],
    targetPromotion = null,
} = {}) => {
    const target = String(targetPromotionId ?? "").trim();
    if (!target || target === "0") {
        return "0";
    }

    const fromLine = resolveCartLinePromotionId(cartItem);
    if (
        fromLine &&
        fromLine !== target &&
        isBuyPromotionCartLineEarly(cartItem)
    ) {
        return fromLine;
    }

    const lines = Array.isArray(cartItems) ? cartItems : [];
    const promoLists = Array.isArray(appliedPromotions) ? appliedPromotions : [];

    let targetPromo = targetPromotion;
    if (!targetPromo) {
        targetPromo =
            promoLists.find(
                (promo) =>
                    String(promo?.id ?? promo?.promotion_id ?? "").trim() === target
            ) || null;
    }

    const targetBuyProductIds = collectPromotionBuyProductIds(targetPromo);
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (resolveCartLinePromotionId(line) !== target) {
            continue;
        }
        if (!isBuyPromotionCartLineEarly(line)) {
            continue;
        }
        const pid = String(line?.product_id ?? line?.productId ?? "").trim();
        if (pid) {
            targetBuyProductIds.add(pid);
        }
    }

    for (let i = 0; i < promoLists.length; i += 1) {
        const promo = promoLists[i];
        const previousId = String(promo?.id ?? promo?.promotion_id ?? "").trim();
        if (!previousId || previousId === "0" || previousId === target) {
            continue;
        }
        const previousBuyIds = collectPromotionBuyProductIds(promo);
        if (buyProductSetsOverlap(targetBuyProductIds, previousBuyIds)) {
            return previousId;
        }
    }

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (!isBuyPromotionCartLineEarly(line)) {
            continue;
        }
        const previousId = resolveCartLinePromotionId(line);
        if (!previousId || previousId === target) {
            continue;
        }

        const pid = String(line?.product_id ?? line?.productId ?? "").trim();
        if (!pid) {
            continue;
        }

        if (targetBuyProductIds.has(pid)) {
            return previousId;
        }
    }

    return "0";
};

export const changePromotion = async ({
    customerId,
    previousPromotionId = "0",
    promotionId,
    channel,
}) => {
    try {
        if (!customerId) {
            throw new Error("Customer ID is required");
        }
        if (promotionId === undefined || promotionId === null || promotionId === "") {
            throw new Error("Promotion ID is required");
        }

        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("customer_id", String(customerId));
        formData.append(
            "previous_promotion_id",
            String(previousPromotionId ?? "0").trim() || "0"
        );
        formData.append("promotion_id", String(promotionId));
        formData.append(
            "channel",
            String(channel ?? getChannelByRole())
        );

        const response = await fetch(`${API_URL}/ws-change-promotion`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error("changePromotion error:", error);
        throw error;
    }
};

/** Map ws-change-promotion response to check-promotion shape for cart merge helpers. */
export const normalizeChangePromotionResponse = (response) => {
    if (!response?.success) {
        return response;
    }
    const data = response.data || {};
    if (!data.items && Array.isArray(data.cart_items)) {
        return {
            ...response,
            data: { ...data, items: data.cart_items },
        };
    }
    return response;
};

export const resolveCartLinePromotionType = (line) => {
    let t = String(
        line?.promo_type ??
        line?.promoType ??
        line?.promosType ??
        ""
    ).trim().toUpperCase();
    if (!t && line?.buyOrGet) {
        const b = String(line.buyOrGet).trim().toUpperCase();
        if (b === "BUY" || b === "GET" || b === "FREE") {
            t = b;
        }
    }
    return t;
};

export const resolveFreshCartLineForPromotionPopup = (cartItems, snapshotLine) => {
    if (!snapshotLine) {
        return null;
    }
    const pid = String(snapshotLine.product_id ?? snapshotLine.productId ?? "").trim();
    if (!pid) {
        return snapshotLine;
    }
    const rows = Array.isArray(cartItems) ? cartItems : [];
    const orderType = String(snapshotLine.order_type ?? snapshotLine.type ?? "")
        .trim()
        .toLowerCase();
    const promoType = resolveCartLinePromotionType(snapshotLine);

    const matches = rows.filter((row) => {
        const rowPid = String(row.product_id ?? row.productId ?? "").trim();
        if (rowPid !== pid) {
            return false;
        }
        if (orderType) {
            const rowOrderType = String(row.order_type ?? row.type ?? "")
                .trim()
                .toLowerCase();
            if (rowOrderType && rowOrderType !== orderType) {
                return false;
            }
        }
        if (promoType) {
            const rowPromoType = resolveCartLinePromotionType(row);
            if (rowPromoType && rowPromoType !== promoType) {
                return false;
            }
        }
        return true;
    });

    if (matches.length === 1) {
        return matches[0];
    }
    if (matches.length > 1) {
        if (promoType) {
            const typed = matches.find((row) => resolveCartLinePromotionType(row) === promoType);
            if (typed) {
                return typed;
            }
        }
        const buyLine = matches.find((row) => resolveCartLinePromotionType(row) === "BUY");
        return buyLine || matches[0];
    }

    const byProduct = rows.filter(
        (row) => String(row.product_id ?? row.productId ?? "").trim() === pid
    );
    if (byProduct.length === 1) {
        return byProduct[0];
    }
    if (byProduct.length > 1 && orderType) {
        const orderMatches = byProduct.filter((row) => {
            const rowOrderType = String(row.order_type ?? row.type ?? "")
                .trim()
                .toLowerCase();
            return !rowOrderType || rowOrderType === orderType;
        });
        if (orderMatches.length === 1) {
            return orderMatches[0];
        }
        if (orderMatches.length > 1) {
            const buyLine = orderMatches.find((row) => resolveCartLinePromotionType(row) === "BUY");
            return buyLine || orderMatches[0];
        }
    }
    return snapshotLine;
};

const rowPromotionIdFromRow = (row) => {
    const id =
        row?.promotion_id ??
        row?.promotionId ??
        row?.id ??
        row?.promo_id ??
        row?.promoId ??
        row?.PromotionId;
    return id != null && id !== "" ? String(id) : "";
};

const rowAppliedFromRow = (row) => {
    if (!row || typeof row !== "object") {
        return false;
    }
    if (row.is_applied === true || row.applied === true || row.is_currently_applied === true) {
        return true;
    }
    if (row.is_applied === 1 || row.applied === 1 || row.is_currently_applied === 1) {
        return true;
    }
    const status = String(row.status ?? "").trim().toLowerCase();
    return status === "applied" || status === "1";
};

export const mergePromotionListWithFallback = (apiRows, fallbackRows) => {
    const api = Array.isArray(apiRows) ? apiRows : [];
    const fallback = Array.isArray(fallbackRows) ? fallbackRows : [];

    if (api.length === 0) {
        return fallback;
    }

    const seen = new Set(api.map(rowPromotionIdFromRow).filter((id) => id !== ""));
    const merged = [...api];
    fallback.forEach((row) => {
        const id = rowPromotionIdFromRow(row);
        if (id && !seen.has(id)) {
            merged.push(row);
            seen.add(id);
        }
    });

    if (fallback.length === 0) {
        return merged;
    }

    const fallbackStatusMap = new Map();
    fallback.forEach((row) => {
        const id = rowPromotionIdFromRow(row);
        if (id) {
            fallbackStatusMap.set(id, rowAppliedFromRow(row));
        }
    });

    return merged.map((row) => {
        const id = rowPromotionIdFromRow(row);
        if (!id) {
            return row;
        }
        const isApplied = fallbackStatusMap.has(id) ? fallbackStatusMap.get(id) : false;
        return {
            ...row,
            is_applied: isApplied,
            applied: isApplied,
            is_currently_applied: isApplied,
        };
    });
};

const isCartLinePromotionActive = (line) => {
    const promoId = resolveCartLinePromotionId(line);
    if (!promoId) {
        return false;
    }
    if (line.promotion_applied === true || line.is_promoted === "1" || line.is_promoted === 1) {
        return true;
    }
    const promoType = resolveCartLinePromotionType(line);
    return promoType === "BUY" || promoType === "GET" || promoType === "FREE";
};

const promotionDisplayId = (promo) =>
    String(promo?.id ?? promo?.rawData?.promotion_id ?? promo?.rawData?.id ?? "").trim();

export const reconcilePromotionDisplayLists = (
    appliedList = [],
    availableList = [],
    cartItems = []
) => {
    const appliedById = new Map();
    (Array.isArray(appliedList) ? appliedList : []).forEach((promo) => {
        const id = promotionDisplayId(promo);
        if (id) {
            appliedById.set(id, promo);
        }
    });

    const availableById = new Map();
    (Array.isArray(availableList) ? availableList : []).forEach((promo) => {
        const id = promotionDisplayId(promo);
        if (id && !availableById.has(id)) {
            availableById.set(id, promo);
        }
    });

    const activeCartPromoIds = new Set();
    (Array.isArray(cartItems) ? cartItems : []).forEach((line) => {
        if (!isCartLinePromotionActive(line)) {
            return;
        }
        const promoId = resolveCartLinePromotionId(line);
        if (promoId) {
            activeCartPromoIds.add(promoId);
        }
    });

    activeCartPromoIds.forEach((promoId) => {
        if (appliedById.has(promoId)) {
            return;
        }
        if (availableById.has(promoId)) {
            appliedById.set(promoId, availableById.get(promoId));
            availableById.delete(promoId);
            return;
        }
        const line = cartItems.find((item) => resolveCartLinePromotionId(item) === promoId);
        const title = line?.promotion_name || line?.promo_name || `Promotion #${promoId}`;
        appliedById.set(promoId, {
            id: promoId,
            title,
            rawData: {
                id: promoId,
                promotion_id: promoId,
                name: title,
            },
        });
    });

    const appliedIds = new Set(appliedById.keys());
    const available = [...availableById.values()].filter(
        (promo) => !appliedIds.has(promotionDisplayId(promo))
    );

    return {
        applied: [...appliedById.values()],
        available,
        appliedCount: appliedById.size,
        totalApplicable: appliedById.size + available.length,
    };
};

export const applyPromotionAppliedOverride = (rows, appliedPromotionId) => {
    const appliedId = appliedPromotionId != null ? String(appliedPromotionId).trim() : "";
    if (!appliedId) {
        return Array.isArray(rows) ? rows : [];
    }
    return (Array.isArray(rows) ? rows : []).map((row) => {
        const rowId = rowPromotionIdFromRow(row);
        if (!rowId) {
            return row;
        }
        const isApplied = rowId === appliedId;
        return {
            ...row,
            is_applied: isApplied,
            applied: isApplied,
            is_currently_applied: isApplied,
        };
    });
};

export const collectProductIdsSharingPromotionWithLine = (cartItems, lineItem) => {
    const clicked = String(lineItem?.product_id ?? lineItem?.productId ?? "").trim();
    if (!clicked) {
        return [];
    }
    const promoId = resolveCartLinePromotionId(lineItem);
    if (!promoId) {
        return [clicked];
    }
    const rows = Array.isArray(cartItems) ? cartItems : [];
    const ids = [];
    const seen = new Set();
    for (const row of rows) {
        if (resolveCartLinePromotionId(row) !== promoId) {
            continue;
        }
        const id = String(row?.product_id ?? row?.productId ?? "").trim();
        if (!id || seen.has(id)) {
            continue;
        }
        seen.add(id);
        ids.push(id);
    }
    return ids.length ? ids : [clicked];
};

const promotionRowFromDisplayPromo = (promo, isApplied) => {
    const raw = promo?.rawData || promo;
    const id = String(raw?.id ?? promo?.id ?? raw?.promotion_id ?? "").trim();
    if (!id || id === "0") {
        return null;
    }
    const title =
        promo?.title ??
        raw?.title ??
        raw?.promotion_name ??
        raw?.promotion_title ??
        raw?.name ??
        "Promotion";
    return {
        ...raw,
        id,
        promotion_id: id,
        promotion_name: title,
        title,
        is_applied: isApplied,
        applied: isApplied,
        is_currently_applied: isApplied,
    };
};

export const cartLineMatchesPromotion = (lineItem, promo) => {
    const itemPid = String(lineItem?.product_id ?? lineItem?.productId ?? "").trim();
    if (!itemPid || !promo) {
        return false;
    }
    const raw = promo.rawData || promo;
    const itemPromoId = resolveCartLinePromotionId(lineItem);
    const promoId = String(raw?.id ?? promo?.id ?? raw?.promotion_id ?? "").trim();
    if (itemPromoId && promoId && itemPromoId === promoId) {
        return true;
    }
    const productLists = [
        raw?.buy_products,
        raw?.get_products,
        raw?.offer_products,
        promo?.buyProducts,
        promo?.getProducts,
        promo?.offerProducts,
    ];
    for (const list of productLists) {
        if (!Array.isArray(list)) {
            continue;
        }
        for (const p of list) {
            const pid = String(
                p?.product_id ?? p?.buy_product_id ?? p?.get_product_id ?? p?.id ?? ""
            ).trim();
            if (pid && pid === itemPid) {
                return true;
            }
        }
    }
    return false;
};

export const buildCartLinePromotionsFallback = (
    lineItem,
    appliedPromotions = [],
    applicablePromotions = []
) => {
    if (!lineItem) {
        return [];
    }
    const rows = [];
    const seen = new Set();
    const push = (promo, applied) => {
        const row = promotionRowFromDisplayPromo(promo, applied);
        if (!row) {
            return;
        }
        const key = String(row.promotion_id ?? row.id);
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        rows.push(row);
    };
    const linkedPromoId = resolveCartLinePromotionId(lineItem);
    const appliedList = Array.isArray(appliedPromotions) ? appliedPromotions : [];
    const applicableList = Array.isArray(applicablePromotions) ? applicablePromotions : [];
    const isPromoAppliedOnLine = (promo) => {
        const raw = promo?.rawData || promo;
        const promoId = String(raw?.id ?? promo?.id ?? raw?.promotion_id ?? "").trim();
        if (linkedPromoId) {
            return Boolean(promoId && linkedPromoId === promoId);
        }
        return raw?.is_currently_applied === true || raw?.applied === true;
    };
    for (const promo of appliedList) {
        if (!cartLineMatchesPromotion(lineItem, promo)) {
            continue;
        }
        push(promo, isPromoAppliedOnLine(promo));
    }
    for (const promo of applicableList) {
        if (!cartLineMatchesPromotion(lineItem, promo)) {
            continue;
        }
        push(promo, isPromoAppliedOnLine(promo));
    }
    if (rows.length === 0 && linkedPromoId) {
        const label =
            lineItem.promotion_name ??
            lineItem.promo_name ??
            lineItem.promos_name ??
            "";
        rows.push({
            id: linkedPromoId,
            promotion_id: linkedPromoId,
            promotion_name: label || `Promotion #${linkedPromoId}`,
            title: label || `Promotion #${linkedPromoId}`,
            is_applied: true,
            applied: true,
            is_currently_applied: true,
        });
    }
    return rows;
};

const collectPromotionsFromCheckResponse = (payload) => {
    const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
    if (!data || typeof data !== "object") {
        return [];
    }
    const rows = [];
    const seen = new Set();
    const push = (list) => {
        if (!Array.isArray(list)) {
            return;
        }
        list.forEach((row) => {
            const id = rowPromotionIdFromRow(row);
            if (!id || seen.has(id)) {
                return;
            }
            seen.add(id);
            rows.push(row);
        });
    };
    push(data.applied_promotions);
    push(data.applicable_promotions);
    push(data.available_promotions);
    const summary = data.promotion_summary;
    if (summary && typeof summary === "object") {
        push(summary.applied_promotions);
        push(summary.applicable_promotions);
        push(summary.available_promotions);
    }
    return rows;
};

export const getCartPromotionsByProduct = async ({
    platform = "5",
    channels = "5",
    customerId,
    productId,
    orderId,
    cartItems = [],
    lineItem = null,
    orderDate = null,
    orderTime = null,
    forEdit = false,
}) => {
    const pidCsv = normalizeProductIdsQueryParam(productId);
    if (!customerId) {
        throw new Error("customerId is required");
    }
    const channel = String(channels || platform || "pos");
    const cartDataToUse = {
        data: Array.isArray(cartItems) ? cartItems : [],
        success: true,
    };
    const payload = transformCartToPromotionPayload(
        cartDataToUse,
        customerId,
        orderDate || formatDateForAPI(),
        channel,
        orderTime,
        orderId
    );
    payload.preview_only = true;
    if (forEdit) {
        payload.for_edit = true;
    }
    const response = await checkProductPromotion(payload);
    const allPromotions = collectPromotionsFromCheckResponse(response);
    const filterLine =
        lineItem ||
        (pidCsv
            ? {
                  product_id: String(pidCsv).split(",")[0],
              }
            : null);
    const filtered = filterLine
        ? allPromotions.filter((promo) => cartLineMatchesPromotion(filterLine, promo))
        : allPromotions;
    return {
        success: response?.success !== false,
        data: {
            applicable_promotions: filtered,
            applied_promotions: filtered.filter((row) => rowAppliedFromRow(row)),
        },
    };
};

export const getCartPromotions = async (platform = 'TELE_CALLING', channels = 'TELE_CALLING', customerId = null) => {
    try {
        const token = localStorage.getItem('token');
        const resolvedPlatform = resolveCartChannelId(platform);
        const resolvedChannels = resolveCartChannelId(channels);
        let url = `${API_URL}/ws-cart-promotions?platform=${resolvedPlatform}&channels=${resolvedChannels}&channel_id=${resolvedChannels}`;
        
        if (customerId) {
            url += `&customer_id=${customerId}`;
        }
        
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}

export const applyCartPromotion = async (promotionId, platform = 'TELE_CALLING', channels = 'TELE_CALLING', customerId = null) => {
    try {
        const token = localStorage.getItem('token');
        const resolvedPlatform = resolveCartChannelId(platform);
        const resolvedChannels = resolveCartChannelId(channels);
        let url = `${API_URL}/ws-cart-promotions?platform=${resolvedPlatform}&channels=${resolvedChannels}&channel_id=${resolvedChannels}&action=apply`;
        
        if (customerId) {
            url += `&customer_id=${customerId}`;
        }
        
        if (promotionId) {
            url += `&promotion_id=${promotionId}`;
        }
        
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}

export const removeCartPromotion = async (promotionId, platform = 'TELE_CALLING', channels = 'TELE_CALLING', customerId = null) => {
    try {
        const token = localStorage.getItem('token');
        const resolvedPlatform = resolveCartChannelId(platform);
        const resolvedChannels = resolveCartChannelId(channels);
        let url = `${API_URL}/ws-cart-promotions?platform=${resolvedPlatform}&channels=${resolvedChannels}&channel_id=${resolvedChannels}&action=remove`;
        
        if (customerId) {
            url += `&customer_id=${customerId}`;
        }
        
        if (promotionId) {
            url += `&promotion_id=${promotionId}`;
        }
        
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}

export const changeGetProduct = async (promotionId, removedProducts, newProducts, platform, customerId, orderId) => {
    try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        
        formData.append('promotion_id', String(promotionId));
        formData.append('platform', String(platform));
        formData.append('customer_id', String(customerId));
        if (orderId !== undefined && orderId !== null && orderId !== '') {
            formData.append('order_id', String(orderId));
        }
        
        removedProducts.forEach((product) => {
            formData.append('remove_product_id[]', String(product.product_id));
            formData.append('remove_product_type[]', String(product.product_type));
            if (product.qty !== undefined && product.qty !== null) {
                formData.append('remove_product_qty[]', String(product.qty));
            }
        });
        
        newProducts.forEach((product) => {
            formData.append('new_product_id[]', String(product.product_id));
            formData.append('new_product_type[]', String(product.product_type));
            if (product.qty !== undefined && product.qty !== null) {
                formData.append('new_product_qty[]', String(product.qty));
            }
            if (product.new_product_unit_price !== undefined && product.new_product_unit_price !== null) {
                formData.append('new_product_unit_price[]', String(product.new_product_unit_price));
            }
        });
        
        const endpoint = `${API_URL}/change_get_product`;
        
        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
            body: formData,
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}